import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { ChevronLeft, Target, Sparkles, TrendingUp, AlertTriangle, Award, Flag } from 'lucide-react';

const COACH_IMG = 'https://customer-assets.emergentagent.com/job_d735c4ed-ebb5-4daa-8cc3-5efce7f3e601/artifacts/j9lk0uwm_Screenshot%202026-06-21%20at%206.49.22%E2%80%AFPM.png';

export default function CoachAnalysis() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/coach-analysis').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-5" data-testid="coach-analysis-page">
      <div className="flex items-center gap-3">
        <button onClick={() => nav(-1)} data-testid="ca-back-btn" className="w-9 h-9 rounded-full bg-[var(--c-film)] flex items-center justify-center"><ChevronLeft className="w-5 h-5" /></button>
        <h1 className="font-display font-black text-2xl">Coach JP Analysis</h1>
      </div>

      {loading && <div className="text-muted-foreground text-sm py-16 text-center">Coach JP is reviewing your progress…</div>}
      {!loading && !data && <div className="text-muted-foreground text-sm py-16 text-center">No analysis available yet. Keep logging your activity.</div>}

      {data && (<>
        {/* 1. Summary */}
        <div className="regen-card-elevated p-5 relative overflow-hidden" data-testid="ca-summary">
          <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-[#A78BFA]/10 blur-2xl" />
          <div className="relative flex items-start gap-3">
            <img src={COACH_IMG} alt="Coach JP" className="w-12 h-12 rounded-xl object-cover ring-2 ring-[#A78BFA]/40 flex-shrink-0 bg-[var(--c-elev)]" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-1">
                <Sparkles className="w-3.5 h-3.5 text-[#A78BFA]" />
                <span className="text-sm font-display font-bold text-[#A78BFA]">Coach JP's Review</span>
                <span className="ml-auto font-mono text-sm text-[var(--c-green-ink)]" data-testid="ca-regen-score">YourReGen {data.regen_score} · {data.level}</span>
              </div>
              <p className="text-sm leading-snug text-foreground/90">{data.summary}</p>
            </div>
          </div>
        </div>

        {/* 2. Today's Focus */}
        {data.today_focus && (
          <div className="regen-card-elevated p-4 flex items-center gap-3" data-testid="ca-today-focus">
            <div className="w-10 h-10 rounded-xl bg-[var(--c-green-soft)] flex items-center justify-center flex-shrink-0"><Target className="w-5 h-5 text-[var(--c-green-ink)]" /></div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--c-green-ink)] font-bold">Today's Focus</div>
              <div className="text-sm font-semibold">{data.today_focus}</div>
            </div>
          </div>
        )}

        {/* 3. Overall YourReGen Review */}
        <div data-testid="ca-pillars">
          <div className="label-mini mb-2">Overall YourReGen Review</div>
          <div className="space-y-2.5">
            {(data.pillars || []).map(p => (
              <div key={p.name} className="regen-card-elevated p-4" data-testid={`ca-pillar-${p.name.toLowerCase()}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-display font-bold">{p.name}</span>
                  <span className="font-mono text-lg font-bold" style={{ color: p.color }}>{p.score}</span>
                </div>
                <div className="h-1.5 rounded-full bg-[var(--c-film)] overflow-hidden mb-2">
                  <div className="h-full rounded-full" style={{ width: `${p.score}%`, background: p.color, boxShadow: `0 0 8px ${p.color}` }} />
                </div>
                <p className="text-xs text-foreground/80">{p.comment}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 4. Goal Progress */}
        {data.goal && (
          <div className="regen-card-elevated p-5" data-testid="ca-goal">
            <div className="flex items-center gap-2 mb-3"><Flag className="w-4 h-4 text-[#00C8FF]" /><span className="font-display font-bold">Goal Progress</span></div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <GoalRow label="Goal" value={data.goal.type} />
              <GoalRow label="Current" value={data.goal.current_weight != null ? `${data.goal.current_weight} kg` : '—'} color="#00C8FF" />
              <GoalRow label="Target" value={data.goal.target_weight != null ? `${data.goal.target_weight} kg` : '—'} color='#65A30D' />
              <GoalRow label={data.goal.direction === 'gain' ? 'To Gain' : 'To Lose'} value={data.goal.remaining != null ? `${data.goal.remaining} kg` : '—'} color="#FFB800" />
              <GoalRow label="Progress" value={data.goal.status} color={data.goal.status === 'On Track' ? '#65A30D' : '#FFB800'} />
            </div>
          </div>
        )}

        {/* 5. Strengths */}
        <div className="regen-card-elevated p-5" data-testid="ca-strengths">
          <div className="flex items-center gap-2 mb-2.5"><Award className="w-4 h-4 text-[var(--c-green-ink)]" /><span className="font-display font-bold">Strengths</span></div>
          <ul className="space-y-1.5">
            {(data.strengths || []).map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm"><TrendingUp className="w-4 h-4 text-[var(--c-green-ink)] flex-shrink-0 mt-0.5" />{s}</li>
            ))}
          </ul>
        </div>

        {/* 6. Areas to Improve */}
        <div className="regen-card-elevated p-5" data-testid="ca-improvements">
          <div className="flex items-center gap-2 mb-2.5"><AlertTriangle className="w-4 h-4 text-[#FFB800]" /><span className="font-display font-bold">Areas to Improve</span></div>
          <ul className="space-y-1.5">
            {(data.improvements || []).map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm"><span className="w-1.5 h-1.5 rounded-full bg-[#FFB800] flex-shrink-0 mt-1.5" />{s}</li>
            ))}
          </ul>
        </div>

        {/* 7. Coach Recommendation */}
        <div className="rounded-2xl bg-[#A78BFA]/10 border border-[#A78BFA]/20 p-5" data-testid="ca-recommendation">
          <div className="flex items-center gap-2 mb-1.5"><Sparkles className="w-4 h-4 text-[#A78BFA]" /><span className="font-display font-bold text-[#A78BFA]">Coach Recommendation</span></div>
          <p className="text-sm leading-snug text-foreground/90">{data.recommendation}</p>
        </div>
      </>)}
    </div>
  );
}

function GoalRow({ label, value, color }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono font-semibold" style={color ? { color } : {}}>{value}</span>
    </div>
  );
}
